import {
  type ModuleActionCode,
  MODULE_ACTIONS_CATALOG,
  PORTAL_MODULES_FOR_ACCESS_MATRIX,
} from './module-actions-catalog';
import {
  Badge,
  Button,
  NativeSelect,
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
  NativeCheckbox,
} from '@giyus/ui';
import { useState } from 'react';

import { useMe } from '../../../lib/auth/authContext';
import {
  ACTIVE_GIYUS_DIRECTORY_USERS,
  type ActiveGiyusDirectoryUser,
  isGreenfieldAdmin,
} from '../../../lib/auth/greenfield';
import {
  type ExperienceMode,
  PERSONA_LENS_META,
  type PersonaLens,
} from '../../../portal/experience/glossary';
import { usePortalExperience } from '../../../portal/experience/PortalExperienceContext';
import { useIdentityUsers, useInviteUser } from '../../identity/api/queries';

export interface UserModulePermissionState {
  readonly hasModuleAccess: boolean;
  readonly role: string;
  readonly actions: readonly ModuleActionCode[];
}

export interface ManagedPortalUser {
  readonly userId: string;
  readonly displayName: string;
  readonly email: string;
  readonly primaryRole: string;
  readonly defaultPersonaLens: PersonaLens;
  readonly defaultVocabularyMode: ExperienceMode;
  readonly isSuperAdmin?: boolean;
}

const DEFAULT_MANAGED_USER: ManagedPortalUser = {
  userId: 'usr-elessandra',
  displayName: 'Elessandra Bitencourt',
  email: 'elessandra@giyus.com.br',
  primaryRole: 'CLINICAL_COORDINATOR',
  defaultPersonaLens: 'operations',
  defaultVocabularyMode: 'plain',
};

const SYLLAS_MANAGED_USER: ManagedPortalUser = {
  userId: 'usr-syllas',
  displayName: 'Syllas Souza (Usuário Master / Root)',
  email: 'syllas@giyus.com.br',
  primaryRole: 'SUPER_ADMIN',
  defaultPersonaLens: 'admin',
  defaultVocabularyMode: 'expert',
  isSuperAdmin: true,
};

const MANAGED_USERS: readonly ManagedPortalUser[] = [
  DEFAULT_MANAGED_USER,
  {
    userId: 'usr-gustavo',
    displayName: 'Gustavo Costa',
    email: 'gustavo.costa@giyus.com.br',
    primaryRole: 'FINANCE_CONTROLLER',
    defaultPersonaLens: 'financial',
    defaultVocabularyMode: 'expert',
  },
  {
    userId: 'usr-marco-trials',
    displayName: 'Dr. Marco Stephan Lofrano Alves (Trials Hub Ltda — EPOCA)',
    email: 'marco.stephan@trialshub.com.br',
    primaryRole: 'PRINCIPAL_INVESTIGATOR',
    defaultPersonaLens: 'clinical',
    defaultVocabularyMode: 'expert',
  },
  SYLLAS_MANAGED_USER,
];

const ALL_ACTION_CODES: readonly ModuleActionCode[] = MODULE_ACTIONS_CATALOG.map((a) => a.code);

function defaultActionsForRole(role: string): readonly ModuleActionCode[] {
  if (role === 'NONE') return [];
  if (role === 'SUPER_ADMIN' || role === 'HOLDING_ADMIN') return ALL_ACTION_CODES;
  if (role === 'MANAGER' || role === 'FINANCE_CONTROLLER' || role === 'CONTRACT_ANALYST') {
    return ['LIST', 'VIEW', 'CREATE', 'EDIT', 'APPROVE', 'EXPORT'];
  }
  if (role === 'CLINICAL_COORDINATOR' || role === 'PRINCIPAL_INVESTIGATOR') {
    return ['LIST', 'VIEW', 'CREATE', 'EDIT', 'EXPORT'];
  }
  return ['LIST', 'VIEW', 'EXPORT'];
}

function buildInitialUserMatrix(
  user: ManagedPortalUser,
): Record<string, UserModulePermissionState> {
  const out: Record<string, UserModulePermissionState> = {};
  for (const mod of PORTAL_MODULES_FOR_ACCESS_MATRIX) {
    const role = user.isSuperAdmin ? 'SUPER_ADMIN' : user.primaryRole;
    out[mod.moduleId] = {
      hasModuleAccess: true,
      role,
      actions: defaultActionsForRole(role),
    };
  }
  return out;
}

function toManagedPortalUser(dirUser: ActiveGiyusDirectoryUser): ManagedPortalUser {
  if (dirUser.email.toLowerCase() === 'syllas@giyus.com.br') {
    return SYLLAS_MANAGED_USER;
  }
  return {
    userId: `usr-${dirUser.id}`,
    displayName: `${dirUser.displayName} (${dirUser.jobTitle})`,
    email: dirUser.email,
    primaryRole: dirUser.primaryRole,
    defaultPersonaLens: dirUser.defaultPersonaLens,
    defaultVocabularyMode: 'plain',
    isSuperAdmin: Boolean(dirUser.isSuperAdmin),
  };
}

export function UserModuleAndActionsAccessMatrix() {
  const me = useMe();
  const greenfield = isGreenfieldAdmin(me);
  const { data: identityUsers } = useIdentityUsers({ kind: 'network' }, {});
  const inviteUser = useInviteUser({ kind: 'network' });
  const [addedDirectoryUsers, setAddedDirectoryUsers] = useState<readonly ManagedPortalUser[]>([]);
  const [directoryAddEmail, setDirectoryAddEmail] = useState<string>('');

  const syncedFromIdentity: readonly ManagedPortalUser[] = (identityUsers ?? [])
    .filter((u) => u.email.toLowerCase().endsWith('@giyus.com.br'))
    .map((u) => {
      const fromCatalog = ACTIVE_GIYUS_DIRECTORY_USERS.find(
        (d) => d.email.toLowerCase() === u.email.toLowerCase(),
      );
      if (fromCatalog) {
        return toManagedPortalUser(fromCatalog);
      }
      return {
        userId: `usr-${u.id}`,
        displayName: u.displayName,
        email: u.email,
        primaryRole: 'CLINICAL_COORDINATOR',
        defaultPersonaLens: 'operations',
        defaultVocabularyMode: 'plain',
      };
    });

  const baseSeed = greenfield ? [SYLLAS_MANAGED_USER] : MANAGED_USERS;
  const baseUsers: readonly ManagedPortalUser[] = (() => {
    const byEmail = new Map<string, ManagedPortalUser>();
    for (const u of [...baseSeed, ...syncedFromIdentity, ...addedDirectoryUsers]) {
      const key = u.email.toLowerCase();
      if (!byEmail.has(key)) {
        byEmail.set(key, u);
      }
    }
    return Array.from(byEmail.values());
  })();

  const { setPersonaLens, setMode } = usePortalExperience();
  const [selectedUserId, setSelectedUserId] = useState<string>(
    greenfield ? SYLLAS_MANAGED_USER.userId : DEFAULT_MANAGED_USER.userId,
  );
  const [deletedUserIds, setDeletedUserIds] = useState<readonly string[]>([]);
  const [userExperienceDefs, setUserExperienceDefs] = useState<
    Record<string, { personaLens: PersonaLens; vocabularyMode: ExperienceMode }>
  >(() => {
    const init: Record<string, { personaLens: PersonaLens; vocabularyMode: ExperienceMode }> = {};
    for (const u of MANAGED_USERS) {
      init[u.userId] = {
        personaLens: u.defaultPersonaLens,
        vocabularyMode: u.defaultVocabularyMode,
      };
    }
    return init;
  });
  const [matricesByUser, setMatricesByUser] = useState<
    Record<string, Record<string, UserModulePermissionState>>
  >(() => {
    const init: Record<string, Record<string, UserModulePermissionState>> = {};
    for (const u of MANAGED_USERS) {
      init[u.userId] = buildInitialUserMatrix(u);
    }
    return init;
  });
  const [savedBanner, setSavedBanner] = useState<string | null>(null);

  const activeUsers = baseUsers.filter((u) => !deletedUserIds.includes(u.userId));
  const activeEmailsSet = new Set(activeUsers.map((u) => u.email.toLowerCase()));
  const availableDirectoryUsersToAdd = ACTIVE_GIYUS_DIRECTORY_USERS.filter(
    (u) => !activeEmailsSet.has(u.email.toLowerCase()),
  );
  const selectedUser: ManagedPortalUser =
    activeUsers.find((u) => u.userId === selectedUserId) ?? activeUsers[0] ?? SYLLAS_MANAGED_USER;
  const userMatrix = matricesByUser[selectedUser.userId] ?? buildInitialUserMatrix(selectedUser);
  const selectedUserDef = userExperienceDefs[selectedUser.userId] ?? {
    personaLens: selectedUser.defaultPersonaLens,
    vocabularyMode: selectedUser.defaultVocabularyMode,
  };

  const handleAddActiveDirectoryUser = () => {
    const targetDir = ACTIVE_GIYUS_DIRECTORY_USERS.find(
      (u) => u.email.toLowerCase() === directoryAddEmail.toLowerCase(),
    );
    if (!targetDir) return;
    const managed = toManagedPortalUser(targetDir);
    setAddedDirectoryUsers((prev) =>
      prev.some((x) => x.email.toLowerCase() === managed.email.toLowerCase())
        ? prev
        : [...prev, managed],
    );
    setDeletedUserIds((prev) => prev.filter((id) => id !== managed.userId));
    setSelectedUserId(managed.userId);
    setDirectoryAddEmail('');
    setSavedBanner(
      `Usuário ativo ${targetDir.displayName} (${targetDir.email}) adicionado e selecionado para configuração de permissões!`,
    );
    inviteUser.mutate({
      personId: targetDir.id,
      email: targetDir.email,
      displayName: targetDir.displayName,
      preferredLocale: 'pt-BR',
    });
  };

  const updateUserDefinition = (
    targetUser: ManagedPortalUser,
    patch: Partial<{ personaLens: PersonaLens; vocabularyMode: ExperienceMode }>,
  ) => {
    setSavedBanner(null);
    setUserExperienceDefs((prev) => {
      const current = prev[targetUser.userId] ?? {
        personaLens: targetUser.defaultPersonaLens,
        vocabularyMode: targetUser.defaultVocabularyMode,
      };
      const updated = { ...current, ...patch };
      try {
        window.localStorage.setItem(
          `giyus.portal.persona_lens:${targetUser.email.toLowerCase()}`,
          updated.personaLens,
        );
        window.localStorage.setItem(
          `giyus.portal.experience_mode:${targetUser.email.toLowerCase()}`,
          updated.vocabularyMode,
        );
      } catch {
        // ignore storage errors
      }
      if (patch.personaLens) setPersonaLens(patch.personaLens);
      if (patch.vocabularyMode) setMode(patch.vocabularyMode);
      return {
        ...prev,
        [targetUser.userId]: updated,
      };
    });
  };

  const deletePersonFromAccess = (targetId: string) => {
    const target = baseUsers.find((u) => u.userId === targetId);
    if (!target || target.isSuperAdmin) return;
    setDeletedUserIds((prev) => {
      const next = prev.includes(targetId) ? prev : [...prev, targetId];
      const remaining = baseUsers.filter((u) => !next.includes(u.userId));
      if (selectedUserId === targetId && remaining[0]) {
        setSelectedUserId(remaining[0].userId);
      }
      return next;
    });
    setSavedBanner(
      `Pessoa excluída com sucesso: ${target.displayName} (${target.email}) foi removida de "Permissões por Área para cada Usuário (@giyus.com.br)" e teve seus acessos revogados.`,
    );
  };

  const restoreAllDeletedPersons = () => {
    setDeletedUserIds([]);
    setSavedBanner('Todas as pessoas excluídas foram restauradas ao gerenciamento de acesso.');
  };

  const updateModuleState = (
    moduleId: string,
    updater: (prev: UserModulePermissionState) => UserModulePermissionState,
  ) => {
    setSavedBanner(null);
    setMatricesByUser((prev) => {
      const currentMatrix = prev[selectedUser.userId] ?? buildInitialUserMatrix(selectedUser);
      const currentMod = currentMatrix[moduleId] ?? {
        hasModuleAccess: true,
        role: selectedUser.primaryRole,
        actions: defaultActionsForRole(selectedUser.primaryRole),
      };
      return {
        ...prev,
        [selectedUser.userId]: {
          ...currentMatrix,
          [moduleId]: updater(currentMod),
        },
      };
    });
  };

  const toggleModuleAccess = (moduleId: string, enabled: boolean) => {
    updateModuleState(moduleId, (prev) => {
      if (!enabled) {
        return { hasModuleAccess: false, role: 'NONE', actions: [] };
      }
      const nextRole = prev.role === 'NONE' ? selectedUser.primaryRole : prev.role;
      return {
        hasModuleAccess: true,
        role: nextRole,
        actions: prev.actions.length > 0 ? prev.actions : defaultActionsForRole(nextRole),
      };
    });
  };

  const toggleAction = (moduleId: string, actionCode: ModuleActionCode, checked: boolean) => {
    updateModuleState(moduleId, (prev) => {
      if (!prev.hasModuleAccess) return prev;
      const set = new Set<ModuleActionCode>(prev.actions);
      if (checked) {
        set.add(actionCode);
      } else {
        set.delete(actionCode);
      }
      return {
        ...prev,
        actions: ALL_ACTION_CODES.filter((c) => set.has(c)),
      };
    });
  };

  const applyPreset = (
    moduleId: string,
    preset: 'ALL' | 'NO_DELETE' | 'READ_ONLY' | 'BLOCK_MODULE',
  ) => {
    updateModuleState(moduleId, (prev) => {
      if (preset === 'BLOCK_MODULE') {
        return { hasModuleAccess: false, role: 'NONE', actions: [] };
      }
      const nextRole = prev.role === 'NONE' ? selectedUser.primaryRole : prev.role;
      const actions: readonly ModuleActionCode[] =
        preset === 'ALL'
          ? ALL_ACTION_CODES
          : preset === 'NO_DELETE'
            ? ['LIST', 'VIEW', 'CREATE', 'EDIT', 'APPROVE', 'EXPORT']
            : ['LIST', 'VIEW', 'EXPORT'];
      return {
        hasModuleAccess: true,
        role: nextRole,
        actions,
      };
    });
  };

  const enabledModulesCount = Object.values(userMatrix).filter((m) => m.hasModuleAccess).length;

  return (
    <section
      className="giyus-card border-2 border-info-border bg-surface p-5"
      data-testid="user-module-and-actions-access-matrix"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="default">GERENCIAMENTO DE ACESSO GRANULAR</Badge>
            <Badge variant="outline">
              {enabledModulesCount}/{PORTAL_MODULES_FOR_ACCESS_MATRIX.length} Módulos Liberados
            </Badge>
          </div>
          <h2 className="mt-1.5 text-body font-bold text-heading">
            Controle de Acesso por Usuário: Acesso ao Módulo + Ações do Módulo (Listar, Visualizar,
            Criar, Editar, Deletar, Aprovar, Exportar)
          </h2>
          <p className="text-muted-foreground mt-1 text-caption">
            Defina para cada usuário tanto se ele <strong>possui acesso ao módulo</strong> quanto{' '}
            <strong>quais ações específicas</strong> ele pode executar dentro do módulo.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <label
            htmlFor="select-matrix-user"
            className="text-caption font-semibold text-foreground-secondary"
          >
            Usuário:
          </label>
          <NativeSelect
            id="select-matrix-user"
            className="border px-2.5 py-1.5 text-caption font-medium"
            value={selectedUser.userId}
            onChange={(e) => {
              setSelectedUserId(e.target.value);
              setSavedBanner(null);
            }}
            data-testid="select-access-matrix-user"
          >
            {activeUsers.map((u) => (
              <option key={u.userId} value={u.userId}>
                {u.displayName} ({u.email})
              </option>
            ))}
          </NativeSelect>
          <Button
            size="sm"
            variant="default"
            onClick={() => {
              setSavedBanner(
                `Permissões salvas para ${selectedUser.displayName} (${selectedUser.email}): ${enabledModulesCount} módulo(s) liberado(s) com ações granulares (Listar, Visualizar, Criar, Editar, Deletar, Aprovar, Exportar) atualizadas!`,
              );
            }}
            data-testid="btn-save-user-module-actions-matrix"
          >
            Salvar Acesso ao Módulo &amp; Ações
          </Button>
          {!selectedUser.isSuperAdmin && (
            <Button
              size="sm"
              variant="destructive"
              onClick={() => {
                deletePersonFromAccess(selectedUser.userId);
              }}
              data-testid="btn-delete-selected-user"
            >
              Excluir Pessoa
            </Button>
          )}
          {deletedUserIds.length > 0 && (
            <Button
              size="sm"
              variant="outline"
              onClick={restoreAllDeletedPersons}
              data-testid="btn-restore-deleted-users"
            >
              Restaurar Pessoa(s) Excluída(s) ({deletedUserIds.length})
            </Button>
          )}
        </div>
      </div>

      {/* DEFINIÇÃO DO USUÁRIO: PERFIL PROFISSIONAL & MODO DE VOCABULÁRIO (SIMPLIFICADO VS. JARGÃO TÉCNICO) */}
      <div
        className="mt-4 rounded-card border border-info-border bg-info-subtle p-3.5"
        data-testid="user-definition-persona-vocabulary-card"
      >
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="text-caption font-bold tracking-wider text-info-subtle-foreground uppercase">
              Definição do Usuário ({selectedUser.displayName}): Tipo de Pessoa &amp; Modo de
              Vocabulário
            </div>
            <p className="mt-0.5 text-caption text-foreground-secondary">
              Esta definição pertence ao cadastro do usuário (não às páginas de conteúdo) e
              determina automaticamente como todos os textos do portal reagem para{' '}
              <strong>{selectedUser.email}</strong>.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <label
              htmlFor="select-user-def-persona"
              className="text-caption font-bold text-foreground-secondary"
            >
              Tipo de Pessoa:
            </label>
            <NativeSelect
              id="select-user-def-persona"
              value={selectedUserDef.personaLens}
              onChange={(e) => {
                updateUserDefinition(selectedUser, {
                  personaLens: e.target.value as PersonaLens,
                });
              }}
              data-testid="select-user-def-persona"
              className="border px-2.5 py-1.5 text-caption font-semibold"
            >
              {(['clinical', 'operations', 'financial', 'admin', 'external'] as const).map(
                (lens) => (
                  <option key={lens} value={lens}>
                    {PERSONA_LENS_META[lens].shortLabel} ({PERSONA_LENS_META[lens].codeTag})
                  </option>
                ),
              )}
            </NativeSelect>

            <label
              htmlFor="select-user-def-mode"
              className="text-caption font-bold text-foreground-secondary"
            >
              Vocabulário do Usuário:
            </label>
            <NativeSelect
              id="select-user-def-mode"
              value={selectedUserDef.vocabularyMode}
              onChange={(e) => {
                updateUserDefinition(selectedUser, {
                  vocabularyMode: e.target.value as ExperienceMode,
                });
              }}
              data-testid="select-user-def-mode"
              className="border px-2.5 py-1.5 text-caption font-semibold"
            >
              <option value="plain">Modo Simplificado (Linguagem Direta)</option>
              <option value="expert">Modo Jargão Técnico (Terminologia Especializada)</option>
            </NativeSelect>
          </div>
        </div>
      </div>

      {savedBanner ? (
        <div
          className="mt-3 rounded-card border border-info-border bg-info-subtle px-3 py-2 text-caption font-semibold text-info-subtle-foreground"
          data-testid="user-module-actions-saved-banner"
        >
          {savedBanner}
        </div>
      ) : null}

      {/* TABELA: PERMISSÕES POR ÁREA PARA CADA USUÁRIO (@giyus.com.br) COM OPÇÃO DE EXCLUIR A PESSOA */}
      <div
        className="mt-4 rounded-card border border-border bg-surface p-3.5"
        data-testid="permissions-by-area-users-card"
      >
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <div>
            <h3 className="text-caption font-bold tracking-wide text-heading uppercase">
              Permissões por Área para cada Usuário (@giyus.com.br)
            </h3>
            <span className="text-caption text-foreground-secondary">
              Selecione um usuário ativo <code>@giyus.com.br</code> para adicionar ou edite as
              permissões existentes.
            </span>
          </div>
          {availableDirectoryUsersToAdd.length > 0 ? (
            <div className="flex flex-wrap items-center gap-2">
              <NativeSelect
                value={directoryAddEmail}
                onChange={(e) => {
                  setDirectoryAddEmail(e.target.value);
                  setSavedBanner(null);
                }}
                aria-label="Selecionar usuário ativo @giyus.com.br para adicionar"
                className="border border-success-border px-2.5 py-1.5 text-caption font-medium"
                data-testid="select-matrix-add-giyus-user"
              >
                <option value="">
                  — Selecionar usuário ativo @giyus.com.br ({availableDirectoryUsersToAdd.length}{' '}
                  ativos) —
                </option>
                {availableDirectoryUsersToAdd.map((u) => (
                  <option key={u.id} value={u.email}>
                    {u.displayName} ({u.email}) — {u.jobTitle}
                  </option>
                ))}
              </NativeSelect>
              <Button
                size="sm"
                disabled={!directoryAddEmail}
                onClick={handleAddActiveDirectoryUser}
                data-testid="btn-matrix-add-giyus-user"
              >
                + Adicionar Usuário (@giyus.com.br)
              </Button>
            </div>
          ) : null}
        </div>
        <div>
          <Table
            regionLabel="Usuários com permissões por área"
            className="w-full border-collapse bg-surface text-left text-caption"
            data-testid="permissions-by-area-users-table"
          >
            <TableHeader>
              <TableRow className="border-b border-border bg-surface-sunken text-caption font-bold text-foreground-secondary uppercase">
                <TableHead className="px-2.5 py-2">Usuário (@giyus.com.br)</TableHead>
                <TableHead className="px-2.5 py-2">Contratos &amp; Orçamentos</TableHead>
                <TableHead className="px-2.5 py-2">Viabilidade &amp; Recrutamento</TableHead>
                <TableHead className="px-2.5 py-2">Financeiro &amp; Contabilidade</TableHead>
                <TableHead className="px-2.5 py-2">Qualidade &amp; Regulatório</TableHead>
                <TableHead className="px-2.5 py-2">Ações (Editar / Excluir Pessoa)</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody className="divide-y divide-border">
              {activeUsers.map((u) => {
                const uMat = matricesByUser[u.userId] ?? buildInitialUserMatrix(u);
                const renderAreaCell = (modId: string) => {
                  const p = uMat[modId];
                  if (!p?.hasModuleAccess) {
                    return <Badge variant="destructive">Sem Acesso</Badge>;
                  }
                  return (
                    <div>
                      <Badge variant="default">{p.role}</Badge>
                      <div className="mt-0.5 text-caption text-foreground-secondary">
                        Ações: {p.actions.join(' · ')}
                      </div>
                    </div>
                  );
                };
                const uDef = userExperienceDefs[u.userId] ?? {
                  personaLens: u.defaultPersonaLens,
                  vocabularyMode: u.defaultVocabularyMode,
                };
                return (
                  <TableRow key={u.userId} data-testid={`area-permissions-user-row-${u.userId}`}>
                    <TableCell className="px-2.5 py-2 font-semibold text-heading">
                      <div>{u.displayName}</div>
                      <div className="font-mono text-caption text-muted-foreground">{u.email}</div>
                      <div className="mt-0.5 text-caption font-bold text-info-subtle-foreground">
                        Definição: {PERSONA_LENS_META[uDef.personaLens].shortLabel} ·{' '}
                        {uDef.vocabularyMode === 'plain'
                          ? 'Modo Simplificado'
                          : 'Modo Jargão Técnico'}
                      </div>
                    </TableCell>
                    <TableCell className="px-2.5 py-2">
                      {renderAreaCell('CONTRATOS_ORCAMENTOS')}
                    </TableCell>
                    <TableCell className="px-2.5 py-2">
                      {renderAreaCell('FEASIBILITY_RECRUTAMENTO')}
                    </TableCell>
                    <TableCell className="px-2.5 py-2">
                      {renderAreaCell('CONTABILIDADE_ACUMULADOR')}
                    </TableCell>
                    <TableCell className="px-2.5 py-2">
                      {renderAreaCell('QUALIDADE_REGULATORIO')}
                    </TableCell>
                    <TableCell className="px-2.5 py-2">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setSelectedUserId(u.userId);
                            setSavedBanner(null);
                          }}
                          data-testid={`btn-select-user-area-permissions-${u.userId}`}
                        >
                          Editar Módulos &amp; Ações
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          disabled={Boolean(u.isSuperAdmin)}
                          onClick={() => {
                            deletePersonFromAccess(u.userId);
                          }}
                          data-testid={`btn-delete-user-area-permissions-${u.userId}`}
                        >
                          Excluir Pessoa
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </div>

      <div className="mt-4">
        <Table
          regionLabel="Matriz de acesso por módulo e ações"
          className="w-full border-collapse text-left text-caption"
        >
          <TableHeader>
            <TableRow className="border-b border-border bg-surface text-caption font-bold text-foreground-secondary uppercase">
              <TableHead className="px-2.5 py-2">Módulo do Sistema</TableHead>
              <TableHead className="px-2.5 py-2">Acesso ao Módulo</TableHead>
              {MODULE_ACTIONS_CATALOG.map((action) => (
                <TableHead
                  key={action.code}
                  className="px-2 py-2 text-center"
                  title={action.description}
                >
                  {action.icon} {action.label}
                </TableHead>
              ))}
              <TableHead className="px-2.5 py-2">Atalhos Rápidos</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody className="divide-y divide-border">
            {PORTAL_MODULES_FOR_ACCESS_MATRIX.map((mod) => {
              const modState = userMatrix[mod.moduleId] ?? {
                hasModuleAccess: true,
                role: selectedUser.primaryRole,
                actions: defaultActionsForRole(selectedUser.primaryRole),
              };
              const hasAccess = modState.hasModuleAccess;
              return (
                <TableRow
                  key={mod.moduleId}
                  className={hasAccess ? 'bg-surface' : 'bg-danger-subtle'}
                  data-testid={`access-matrix-row-${mod.moduleId}`}
                >
                  <TableCell className="px-2.5 py-2.5 font-semibold text-heading">
                    <div>{mod.title}</div>
                    <span className="font-mono text-caption text-muted-foreground">
                      {mod.moduleId}
                    </span>
                  </TableCell>
                  <TableCell className="px-2.5 py-2.5">
                    <label className="flex cursor-pointer items-center gap-1.5 font-semibold">
                      <NativeCheckbox
                        checked={hasAccess}
                        onChange={(e) => {
                          toggleModuleAccess(mod.moduleId, e.target.checked);
                        }}
                        data-testid={`chk-module-access-${mod.moduleId}`}
                      />
                      <Badge variant={hasAccess ? 'default' : 'destructive'}>
                        {hasAccess ? `Liberado (${modState.role})` : 'Sem Acesso'}
                      </Badge>
                    </label>
                  </TableCell>
                  {MODULE_ACTIONS_CATALOG.map((action) => {
                    const checked = hasAccess && modState.actions.includes(action.code);
                    return (
                      <TableCell key={action.code} className="px-2 py-2.5 text-center">
                        <label className="inline-flex cursor-pointer items-center gap-1 font-medium">
                          <NativeCheckbox
                            checked={checked}
                            disabled={!hasAccess}
                            onChange={(e) => {
                              toggleAction(mod.moduleId, action.code, e.target.checked);
                            }}
                            data-testid={`chk-module-action-${mod.moduleId}-${action.code}`}
                          />
                          <span
                            className={
                              checked ? 'text-success-subtle-foreground' : 'text-muted-foreground'
                            }
                          >
                            {checked ? 'Sim' : 'Não'}
                          </span>
                        </label>
                      </TableCell>
                    );
                  })}
                  <TableCell className="px-2.5 py-2.5">
                    <div className="flex flex-wrap gap-1">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          applyPreset(mod.moduleId, 'ALL');
                        }}
                        data-testid={`btn-preset-all-${mod.moduleId}`}
                      >
                        Todas
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          applyPreset(mod.moduleId, 'NO_DELETE');
                        }}
                        data-testid={`btn-preset-nodelete-${mod.moduleId}`}
                      >
                        S/ Deletar
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          applyPreset(mod.moduleId, 'READ_ONLY');
                        }}
                        data-testid={`btn-preset-readonly-${mod.moduleId}`}
                      >
                        Só Listar/Ver
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          applyPreset(mod.moduleId, 'BLOCK_MODULE');
                        }}
                        data-testid={`btn-preset-block-${mod.moduleId}`}
                      >
                        Bloquear
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </section>
  );
}
